import React, { useEffect, useState } from 'react';
import { Info, X } from 'lucide-react';
import './InfoBanner.css';

interface Broadcast {
  id: number;
  title: string;
  content: string;
  sender_name?: string;
  sender_alias?: string;
  created_at: string;
}

interface InfoBannerProps {
  broadcast: Broadcast | null;
  onDismiss: (id: number) => void;
  autoHideDuration?: number; // milliseconds, 0 = no auto-hide
}

const InfoBanner: React.FC<InfoBannerProps> = ({ 
  broadcast, 
  onDismiss,
  autoHideDuration = 10000 // Default 10 seconds
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (broadcast) {
      // Trigger animation
      setIsVisible(true);

      // Auto-hide after duration
      if (autoHideDuration > 0) {
        const timer = setTimeout(() => {
          handleDismiss();
        }, autoHideDuration);

        return () => clearTimeout(timer);
      }
    } else {
      setIsVisible(false);
    }
  }, [broadcast, autoHideDuration]);

  const handleDismiss = () => {
    setIsVisible(false);
    // Wait for animation to complete before calling onDismiss
    setTimeout(() => {
      if (broadcast) {
        onDismiss(broadcast.id);
      }
    }, 300);
  };

  if (!broadcast) return null;

  const senderDisplay = broadcast.sender_alias || broadcast.sender_name || 'Sistema';

  return (
    <div className={`info-banner ${isVisible ? 'visible' : ''}`}>
      <div className="info-banner-container">
        <div className="info-banner-icon">
          <Info size={20} />
        </div>

        <div className="info-banner-content">
          <div className="info-banner-title">{broadcast.title}</div>
          <div className="info-banner-text">{broadcast.content}</div>
          <div className="info-banner-sender">— {senderDisplay}</div>
        </div>

        <button className="info-banner-close" onClick={handleDismiss} title="Cerrar">
          <X size={18} />
        </button>
      </div>

      {/* Progress bar for auto-hide */}
      {autoHideDuration > 0 && (
        <div 
          className="info-banner-progress" 
          style={{ animationDuration: `${autoHideDuration}ms` }}
        />
      )}
    </div>
  );
};

// Global instance for WebSocket to trigger info broadcasts
let infoBannerInstance: {
  show: (broadcast: Broadcast) => void;
} | null = null;

export const InfoBannerController = () => {
  const [broadcast, setBroadcast] = React.useState<Broadcast | null>(null);

  React.useEffect(() => {
    infoBannerInstance = {
      show: (newBroadcast: Broadcast) => {
        setBroadcast(newBroadcast);
      }
    };

    // Expose globally for WebSocket
    (window as any).infoBanner = {
      show: (newBroadcast: Broadcast) => {
        infoBannerInstance?.show(newBroadcast);
      }
    };

    return () => {
      infoBannerInstance = null;
      delete (window as any).infoBanner;
    };
  }, []);

  const handleDismiss = async (id: number) => {
    try {
      const response = await fetch(`/api/broadcast/${id}/dismiss`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        console.error('Failed to dismiss info broadcast');
      }
    } catch (error) {
      console.error('Error dismissing info broadcast:', error);
    } finally {
      // Always clear the broadcast
      setBroadcast(null);
    }
  };

  return <InfoBanner broadcast={broadcast} onDismiss={handleDismiss} autoHideDuration={10000} />;
};

export default InfoBanner;
